import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import DateRangePicker from '@/components/ui/DateRangePicker';
import PagesWrapper from '@/components/ui/shared/PagesWrapper';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, X } from 'lucide-react';
import ListChartWidget from '../ListChartWidget';
import ColumnPieChart from '../ColumnPieChart';
import Claim from '../CompanyOverviewClaim';
import { userService } from '../../_services';
import { Redirect } from "react-router-dom";
import ColorJson from '../../../src/whitelabel.json';
import { safeParseDate } from '@/lib/utils';

function CompanyOverview() {
    // Initialize dates
    const defaultStartDate = useMemo(() => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return date;
    }, []);
    
    const defaultEndDate = useMemo(() => new Date(), []);

    const getInitialDates = useCallback(() => {
        const storedStartDate = localStorage.getItem('User_Start_Date');
        const storedEndDate = localStorage.getItem('User_End_Date');
        
        const startdate = (storedStartDate && storedStartDate !== "" && storedStartDate !== "null")
            ? safeParseDate(storedStartDate, defaultStartDate)
            : defaultStartDate;
        const enddate = (storedEndDate && storedEndDate !== "" && storedEndDate !== "null")
            ? safeParseDate(storedEndDate, defaultEndDate)
            : defaultEndDate;
        
        return {
            startDate: format(startdate, "MM/dd/yyyy"),
            endDate: format(enddate, "MM/dd/yyyy")
        };
    }, [defaultStartDate, defaultEndDate]);

    const initialDates = useMemo(() => getInitialDates(), [getInitialDates]);

    // State management
    const [startDate, setStartDate] = useState(initialDates.startDate);
    const [endDate, setEndDate] = useState(initialDates.endDate);
    const [graphData, setGraphData] = useState([]);
    const [searchDisplay, setSearchDisplay] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [filterTags, setFilterTags] = useState(null);
    const [filterUpdateTrigger, setFilterUpdateTrigger] = useState(0);
    
    const [userdata] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    });

    const searchInputRef = useRef(null);
    const searchResultsRef = useRef(null);

    // Load filter tags from localStorage
    const loadFilterTags = useCallback(() => {
        try {
            const filterJson = localStorage.getItem('Overview_Filter');
            if (filterJson && filterJson !== 'null' && filterJson !== '{}') {
                setFilterTags(JSON.parse(filterJson));
            } else {
                setFilterTags(null);
            }
        } catch {
            setFilterTags(null);
        }
    }, []);

    // Change data function (defined before handleCalendar since it's referenced there)
    const changeData = useCallback((dateChanged, stdate, endate) => {
        try {
            const startDateObj = dateChanged ? stdate : startDate;
            const endDateObj = dateChanged ? endate : endDate;
            
            // Safely parse dates with fallback to defaults
            const parsedStartDate = safeParseDate(startDateObj, defaultStartDate);
            const parsedEndDate = safeParseDate(endDateObj, defaultEndDate);
            
            const startDateFormatted = format(parsedStartDate, "yyyyMMdd");
            const endDateFormatted = format(parsedEndDate, "yyyyMMdd");
            const changedStartDate = format(parsedStartDate, "MM/dd/yyyy");
            const changedEndDate = format(parsedEndDate, "MM/dd/yyyy");
            
            setStartDate(changedStartDate);
            setEndDate(changedEndDate);

        const APIBaseURL = '/api/ComOverview';
        let AdFilterJson = null;
        try {
            AdFilterJson = JSON.parse(localStorage.getItem('Overview_Filter'));
        } catch {
            AdFilterJson = null;
        }

        const Widget1 = JSON.stringify({
            wname: "",
            whname: "",
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            chart_type: "list",
            rcount: 30,
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type')
        });

        const Widget2 = JSON.stringify({
            wname: "",
            whname: "",
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            chart_type: "list",
            rcount: 30,
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type')
        });

        const Widget3 = JSON.stringify({
            wname: "",
            whname: "",
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            chart_type: "list",
            rcount: 30,
            ispatientsaving: "true",
            pharmacynumber: "all",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type')
        });

        const Day_Supply_30_90_RequestJson = JSON.stringify({
            wname: String(userdata?.wholesaler_Id || ""),
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            rcount: 30,
            chart_type: "pielist",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: userdata?.wholesaler_Id || ""
        });

        const Member_Age_RequestJson = JSON.stringify({
            wname: String(userdata?.wholesaler_Id || ""),
            start_date: startDateFormatted,
            end_date: endDateFormatted,
            rcount: 30,
            chart_type: "pielist",
            filtertime: localStorage.getItem('Filter_Last_Update_Time'),
            rxsClaimType: localStorage.getItem('Updated_HD_Type'),
            wholesaler_id: userdata?.wholesaler_Id || ""
        });

        // Determine colors based on host
        const host = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        const isRxHacker = host === "rxhacker.com" || 
                          host === "localhost:5464" || 
                          window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
                          window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
        
        const secondaryColor = isRxHacker 
            ? ColorJson.ApplicationColor.Rxhacker_secondary_color 
            : ColorJson.ApplicationColor.Glic_secondary_color;
        const extraColor = isRxHacker 
            ? ColorJson.ApplicationColor.Rxhacker_extra_color 
            : ColorJson.ApplicationColor.Glic_extra_color;

        setGraphData([
            {
                url: APIBaseURL + "/Number_Statistics",
                type: "Claim",
                title: "Claims Statistics",
                Subtitle: "Overview of claims data",
                ColorCode: extraColor,
                id: "RxSensClaim",
                RequestJson: Widget3,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/state_wise_paid_claims",
                type: "List",
                title: 'Paid Claims by State',
                Subtitle: 'Total paid claims broken down by state',
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "",
                YlabelString: "",
                id: "ComOverview_widget1",
                RequestJson: Widget1,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/pharmacy_chain_paid_claims",
                type: "List",
                title: 'Paid Claims (%) by Pharmacy Chain',
                Subtitle: 'Paid claims % broken down by Pharmacy Chain',
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "",
                YlabelString: "",
                id: "ComOverview_pharmacy_chain_paid_claims",
                RequestJson: Widget1,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/drug_wise_high_savings",
                type: "List",
                title: 'Highest Script Savings',
                Subtitle: 'Top 10 prescriptions with the highest savings',
                ColorCode: secondaryColor,
                DataType: "NA",
                XlabelString: "",
                YlabelString: "",
                id: "ComOverview_widget2",
                RequestJson: Widget2,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/day_supply_30_90",
                type: "Column_Pie",
                title: "30/90 Day Supply",
                Subtitle: "% of prescription days supply",
                XlabelString: "",
                YlabelString: "",
                ColorCode: "#ffba10",
                id: "Day_Supply_30_90",
                RequestJson: Day_Supply_30_90_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            },
            {
                url: APIBaseURL + "/member_age",
                type: "Column_Pie",
                title: "Member Age",
                Subtitle: "Age of Members filling prescriptions",
                XlabelString: "",
                YlabelString: "",
                ColorCode: "#ffba10",
                id: "Member_Age",
                RequestJson: Member_Age_RequestJson,
                RequestJson1: JSON.stringify(AdFilterJson)
            }
        ]);
        } catch (error) {

        }
    }, [startDate, endDate, userdata, defaultStartDate, defaultEndDate]);

    // Handle calendar date change
    const handleCalendar = useCallback((e, picker) => {
        try {
            const changedStartDate = format(safeParseDate(picker.startDate, defaultStartDate), "MM/dd/yyyy");
            const changedEndDate = format(safeParseDate(picker.endDate, defaultEndDate), "MM/dd/yyyy");
            localStorage.setItem('User_Start_Date', changedStartDate);
            localStorage.setItem('User_End_Date', changedEndDate);
            setStartDate(changedStartDate);
            setEndDate(changedEndDate);
            changeData(true, changedStartDate, changedEndDate);
        } catch (error) {

        }
    }, [changeData, defaultStartDate, defaultEndDate]);

    // Handle search input change
    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchValue(value);
        
        if (value.length >= 2) {
            setShowSearchResults(true);
            const authData = userdata?.authdata;
            if (authData) {
                userService.CompnayOverViewSearch(authData, value).then(
                    apidata => {
                        try {
                            setSearchDisplay(apidata);
                        } catch {
                            setSearchDisplay(null);
                        }
                    }
                ).catch(() => {
                    setSearchDisplay(null);
                });
            }
        } else {
            setSearchDisplay(null);
            setShowSearchResults(false);
        }
    }, [userdata]);

    // Repeat code (refresh data)
    const repeatCode = useCallback(() => {
        try {
            let startdate = new Date();
            startdate.setDate(startdate.getDate() - 30);
            let enddate = new Date();
            
            const storedStart = localStorage.getItem('User_Start_Date');
            const storedEnd = localStorage.getItem('User_End_Date');
            
            if (storedStart && storedStart !== "" && storedStart !== null && storedStart !== undefined) {
                startdate = storedStart;
            }
            if (storedEnd && storedEnd !== "" && storedEnd !== null && storedEnd !== undefined) {
                enddate = storedEnd;
            }
            
            const formattedStart = format(safeParseDate(startdate), "MM/dd/yyyy");
            const formattedEnd = format(safeParseDate(enddate), "MM/dd/yyyy");
            localStorage.setItem('Filter_Last_Update_Time', new Date());
            changeData(true, formattedStart, formattedEnd);
        } catch {
            // Handle error
        }
    }, [changeData]);

    // Create advance filter JSON
    const createAdvanceFilterJson = useCallback((nodetype, nodevalue) => {
        let Created_Advance_Filter = null;
        try {
            Created_Advance_Filter = JSON.parse(localStorage.getItem('Overview_Filter'));
        } catch {
            Created_Advance_Filter = null;
        }

        if (Created_Advance_Filter && Created_Advance_Filter !== '' && Created_Advance_Filter !== "null" && Created_Advance_Filter !== "{}") {
            const arr = [];
            Object.keys(Created_Advance_Filter).forEach(key => arr.push({ Name: key, Value: Created_Advance_Filter[key] }));
            
            let IsMatched = false;
            for (let ij = 0; ij < arr.length; ij++) {
                if (arr[ij].Name === nodetype) {
                    IsMatched = true;
                    let checkavail = 0;
                    Created_Advance_Filter[nodetype].forEach((e1) => {
                        if (e1.toLowerCase() === nodevalue.toLowerCase()) {
                            checkavail = checkavail + 1;
                        }
                    });
                    if (checkavail === 0) {
                        Created_Advance_Filter[nodetype].push(nodevalue);
                        localStorage.setItem('Overview_Filter', JSON.stringify(Created_Advance_Filter));
                        const newDate = new Date();
                        localStorage.setItem('An_Overview_Last_Update_Time', newDate);
                        localStorage.setItem('An_Updated_AdOverview_Status', JSON.stringify(Created_Advance_Filter));
                        loadFilterTags();
                        repeatCode();
                    }
                    break;
                }
            }
            
            if (!IsMatched) {
                try {
                    const JsonData = `"${nodetype}": ["${nodevalue}"]`;
                    let TempJson = JSON.stringify(Created_Advance_Filter).replace('{', '').replace('}', '');
                    TempJson = '{' + TempJson + ',' + JsonData + '}';
                    localStorage.setItem('Overview_Filter', TempJson);
                    const newDate = new Date();
                    localStorage.setItem('An_Overview_Last_Update_Time', newDate);
                    localStorage.setItem('An_Updated_AdOverview_Status', TempJson);
                    loadFilterTags();
                    repeatCode();
                } catch {
                    // Handle error
                }
            }
        } else {
            try {
                const JsonData = `"${nodetype}": ["${nodevalue}"]`;
                const fullJson = `{"dataset": {${JsonData}}}`;
                localStorage.setItem('Overview_Filter', JSON.stringify(JSON.parse(fullJson)["dataset"]));
                const newDate = new Date();
                localStorage.setItem('An_Overview_Last_Update_Time', newDate);
                localStorage.setItem('An_Updated_AdOverview_Status', JSON.stringify(Created_Advance_Filter));
                loadFilterTags();
                repeatCode();
            } catch {
                // Handle error
            }
        }
    }, [loadFilterTags, repeatCode]);

    // Handle search item click
    const handleSearchItemClick = useCallback((item) => {
        try {
            setSearchValue('');
            setSearchDisplay([]);
            setShowSearchResults(false);
            
            const nodetype = item.typeName;
            const nodevalue = item.o_name;
            
            createAdvanceFilterJson(nodetype, nodevalue);
            
            // Get dates and refresh data
            let startdate = new Date();
            startdate.setDate(startdate.getDate() - 30);
            let enddate = new Date();
            
            const storedStart = localStorage.getItem('User_Start_Date');
            const storedEnd = localStorage.getItem('User_End_Date');
            
            if (storedStart && storedStart !== "" && storedStart !== null && storedStart !== undefined) {
                startdate = storedStart;
            }
            if (storedEnd && storedEnd !== "" && storedEnd !== null && storedEnd !== undefined) {
                enddate = storedEnd;
            }
            
            const formattedStart = format(safeParseDate(startdate), "MM/dd/yyyy");
            const formattedEnd = format(safeParseDate(enddate), "MM/dd/yyyy");
            changeData(true, formattedStart, formattedEnd);
        } catch (error) {

        }
    }, [createAdvanceFilterJson, changeData]);

    // Get tag type full name
    const getTagTypeFullName = useCallback((stext) => {
        if (stext === 'Pharmacy Chain') {
            return 'Pharmacy Chain';
        } else if (stext === 'Wholesaler Name') {
            return 'Wholesaler Name';
        } else {
            return stext;
        }
    }, []);

    // Handle remove tag
    const handleRemoveTag = useCallback((nodename, nodevalue) => {
        try {
            let ExistJson = null;
            try {
                ExistJson = JSON.parse(localStorage.getItem('Overview_Filter'));
            } catch {
                ExistJson = null;
            }
            
            if (ExistJson && ExistJson[nodename]) {
                const ItemIndex = ExistJson[nodename].indexOf(nodevalue);
                if (ItemIndex > -1) {
                    ExistJson[nodename].splice(ItemIndex, 1);
                    if (ExistJson[nodename].length === 0) {
                        delete ExistJson[nodename];
                    }
                }
                
                if (JSON.stringify(ExistJson) === "{}") {
                    localStorage.setItem('Overview_Filter', null);
                } else {
                    localStorage.setItem('Overview_Filter', JSON.stringify(ExistJson));
                }
                
                loadFilterTags();
                repeatCode();
            }
        } catch {
            // Handle error
        }
    }, [loadFilterTags, repeatCode]);

    // Handle reset button
    const handleResetButton = useCallback(() => {
        try {
            localStorage.setItem('Overview_Filter', null);
            loadFilterTags();
            repeatCode();
        } catch {
            // Handle error
        }
    }, [loadFilterTags, repeatCode]);

    // Click outside handler for search results
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchResultsRef.current && !searchResultsRef.current.contains(event.target) &&
                searchInputRef.current && !searchInputRef.current.contains(event.target)) {
                setShowSearchResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Initialize on mount
    useEffect(() => {
        loadFilterTags();
        changeData();
    }, [changeData, loadFilterTags]);

    // Monitor filter changes
    useEffect(() => {
        if (filterUpdateTrigger > 0) {
            loadFilterTags();
            changeData();
        }
    }, [filterUpdateTrigger, loadFilterTags, changeData]);

    // Check authentication and user type
    if (userService.Check_LoggedIn_Status() === 'Login') {
        return <Redirect to="/login" />;
    }

    const usertype = userService.Get_User_Type();
    if (usertype?.toLowerCase() === 'wholesaler' || 
        usertype?.toLowerCase() === 'distributor' || 
        usertype?.toLowerCase() === 'subadmin') {
        return <Redirect to='/pageNotFound' />;
    }

    const hasFilterTags = filterTags && Object.keys(filterTags).length > 0;

    // Separate claim widget from other widgets for layout
    const claimWidget = graphData.find(item => item.type === "Claim");
    const otherWidgets = graphData.filter(item => item.type !== "Claim");

    return (
        <PagesWrapper>
            <div className="flex flex-col gap-4">
                {/* Page Header */}
                <div className="flex items-center">
                    <h1 className="text-2xl font-semibold text-foreground">
                        Company Overview (RXS only)
                    </h1>
                </div>

                {/* Filters Card */}
                <Card>
                    <CardContent className="analytics-card-content">
                        <div className="flex flex-col lg:flex-row gap-4 flex-wrap">
                            <DateRangePicker 
                                className="w-[320px] max-w-full h-10"
                                initialSettings={{
                                    startDate: startDate,
                                    endDate: endDate,
                                    maxDate: new Date(),
                                    locale: {
                                        format: 'MM/DD/YYYY'
                                    }
                                }}
                                onApply={handleCalendar}
                            >
                                <div className="flex items-center gap-0 w-[320px] max-w-full rounded-md overflow-hidden border border-input bg-card hover:shadow-sm transition-shadow h-10">
                                    <div className="flex items-center gap-1 px-3 flex-1">
                                        <span className="text-sm text-muted-foreground whitespace-nowrap">From</span>
                                        <Input
                                            type="text"
                                            className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                            value={startDate}
                                            readOnly
                                        />
                                    </div>
                                    <div className="h-6 w-px bg-border flex-shrink-0" />
                                    <div className="flex items-center gap-1 px-3 flex-1">
                                        <span className="text-sm text-muted-foreground whitespace-nowrap">To</span>
                                        <Input
                                            type="text"
                                            className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                            value={endDate}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </DateRangePicker>

                            {/* Search - flexible width, grows to fill space */}
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search for a Wholesaler or Pharmacy Chain"
                                    className="searchFilter pl-10 w-full"
                                    value={searchValue}
                                    onChange={handleSearchChange}
                                    autoComplete="off"
                                />
                                {showSearchResults && searchDisplay && searchDisplay.length > 0 && (
                                    <div 
                                        ref={searchResultsRef}
                                        className="absolute z-50 w-full bg-popover border rounded-md shadow-lg mt-1 max-h-60 overflow-auto"
                                    >
                                        {searchDisplay.map((item, index) => (
                                            <div 
                                                key={index}
                                                className="flex items-center justify-between p-3 hover:bg-accent cursor-pointer text-sm border-b last:border-b-0"
                                                onClick={() => handleSearchItemClick(item)}
                                            >
                                                <span className="font-medium">{item.o_name}</span>
                                                <span className="text-muted-foreground">{item.typeName}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Filter Tags - Outside Card */}
                {hasFilterTags && (
                    <div className="flex items-start gap-4">
                        {/* Scrollable tags container */}
                        <div className="overflow-x-auto">
                            <div className="flex items-center gap-4 min-w-min">
                        {Object.keys(filterTags).flatMap((key) => 
                            filterTags[key].map((value, i1) => (
                                <Badge
                                    key={`${key}-${value}-${i1}`}
                                    variant="outline"
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-normal leading-none whitespace-nowrap bg-primary/10 text-primary border-primary"
                                >
                                    {getTagTypeFullName(key)}: {value}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveTag(key, value)}
                                        className="h-4 w-4 p-0 hover:bg-transparent hover:text-current [&_svg]:size-2.5"
                                    >
                                        <X />
                                    </Button>
                                </Badge>
                            ))
                        )}
                            </div>
                        </div>
                        {/* Reset button - fixed outside scroll */}
                        <Button
                            variant="ghost" className="h-auto py-1 px-3 gap-1.5 whitespace-nowrap flex-shrink-0 leading-[1.15] bg-foreground text-card border border-foreground hover:bg-foreground hover:text-card hover:opacity-85"
                            onClick={handleResetButton}
                        >
                            Reset
                        </Button>
                    </div>
                )}

                {/* Claims Statistics Widget - Full Width Grid */}
                {claimWidget && (
                    <Claim 
                        data={claimWidget} 
                        _Index={0} 
                    />
                )}

                {/* Other Widgets Grid */}
                <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                    {otherWidgets.map((item, index) => (
                        <div key={`graph-widget-${index}`} className="min-h-[400px]">
                            {item.type === "List" && (
                                <ListChartWidget
                                    data={item}
                                    _Index={index}
                                    hideLegend={true}
                                />
                            )}
                            {item.type === "Column_Pie" && (
                                <ColumnPieChart
                                    data={item}
                                    _Index={index}
                                    hideLegend={true}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </PagesWrapper>
    );
}

export default CompanyOverview;
